import { get, isEqual, noop, throttle } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css, withTheme } from 'styled-components';

import { Checkbox as UnstyledCheckbox } from '@zocdoc/components/src/Checkbox';
import { grey75, navy, white } from '@zocdoc/branding/colors';
import {
    Breakpoints,
    HideFor,
    ShowFor,
    breakpoint,
    getWindowBreakpoint,
} from '@zocdoc/components/src/grid';

import {
    Arrow as UnstyledArrow,
    Switchboard as UnstyledSwitchboard,
    Dot as UnstyledDot,
} from '@zocdoc/components/src/icons';

import { Markdown as UnstyledMarkdown } from '@zocdoc/components/src/Markdown';
import * as Layout from '@zocdoc/components/src/Layout';
import ScrollLock from '@zocdoc/components/src/ScrollLock';
import * as SEO from '@zocdoc/components/src/Seo';
import UnstyledSpinner from '@zocdoc/components/src/Spinner';
import { getLogoSizingAtBreakpoint } from '@zocdoc/components/src/themes/helpers';
import { FontFamily } from '@zocdoc/components/src/typography';
import { StickyContainer, Sticky } from '@zocdoc/react-sticky-5';

import * as ViewConstants from '../constants';
import NoResults from './NoResults';
import RefineSearch from './RefineSearch';
import GovernmentInsuranceNotice from './GovernmentInsuranceNotice';
import { VisitReasonsDropdown as UnstyledVisitReasonsDropdown } from './RefineSearch/VisitReasons';
import { SpecialtiesDropdown as UnstyledSpecialtiesDropdown } from './RefineSearch/Specialties';
import { SearchMap, POSITION as MapControlsPosition } from './SearchMap';
import SearchPagination from './SearchPagination';
import SearchResults from './SearchResults';
import MobileHeaderToggles from '../../../Shared/MobileHeaderToggles';
import FacetedSearch from './FacetedSearch';
import SortingTakeover from './FacetedSearch/SortingTakeover';

const SORT_BY = 'Sort by';
const VIEW_MAP = 'View map';

export default class SearchResultsWithMap extends Component {
    static propTypes = {
        /**
         * overrides for screenshot testing
         */
        __showMobileMap: PropTypes.bool,
        __showRefinePanel: PropTypes.bool,

        additionalProfileUrlParams: PropTypes.object,
        acceptedProcedures: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.string,
        })),
        disableProcedureCache: PropTypes.bool,
        doctorLocationIds: PropTypes.array,
        error: PropTypes.object,
        facets: PropTypes.array,
        insuranceCarrierImage: PropTypes.string,
        isNewPatient: PropTypes.bool,
        loading: PropTypes.bool.isRequired,
        page: PropTypes.number,
        pageCount: PropTypes.number,
        providers: PropTypes.array,
        providersMappedByNetworkType: PropTypes.object,
        refinementCount: PropTypes.number,
        searchAddress: PropTypes.string,
        searchProcedureId: PropTypes.string,
        searchRequestId: PropTypes.string,
        searchSpecialtyId: PropTypes.string,
        seo: PropTypes.shape({
            title: PropTypes.string,
            blurb: PropTypes.shape({
                header: PropTypes.string,
                description: PropTypes.string,
            }),
        }),
        shouldRenderFacetedSearchBar: PropTypes.bool,
        shouldRenderSearchOverlay: PropTypes.bool,
        shouldRenderNetworkType: PropTypes.bool,
        shouldRenderRefineSearchButton: PropTypes.bool,
        shouldRenderSearchByMovingMap: PropTypes.bool,
        showGovernmentInsuranceNotice: PropTypes.bool,
        showInsurancePicker: PropTypes.func,
        specialties: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
        })),
        telehealthAds: PropTypes.array,
        theme: PropTypes.object.isRequired,
        totalCount: PropTypes.number,
        trackSortingDropdownClick: PropTypes.func,
        updateParameters: PropTypes.func,
        viewPageMessage: PropTypes.object,
        visitReasons: PropTypes.array.isRequired,
    };

    static defaultProps = {
        __showMobileMap: false,
        __showRefinePanel: false,

        facets: [],
        isNewPatient: true,
        shouldRenderFacetedSearchBar: false,
        shouldRenderNetworkType: true,
        shouldRenderRefineSearchButton: true,
        shouldRenderSearchByMovingMap: true,
        showInsurancePicker: noop,
        specialties: [],
        trackSortingDropdownClick: noop,
        providers: [],
        telehealthAds: [],
        viewPageMessage: (
            <FormattedMessage
                defaultMessage="View list"
                id="Components.Search.MobileMapToggleActive"
            />
        ),
    };

    static shouldLockScrolling(shouldRenderRefinePanel, shouldRenderMobileMap) {
        return (
            (shouldRenderRefinePanel || shouldRenderMobileMap)
            && getWindowBreakpoint() < Breakpoints.small
        );
    }

    static getDerivedStateFromProps(nextProps, state) {
        const nextState = { ...state };

        /* eslint-disable no-multi-assign */

        if (nextProps.__showRefinePanel !== state.__showRefinePanel) {
            nextState.shouldRenderRefinePanel = nextState.__showRefinePanel = nextProps.__showRefinePanel;
        }

        if (nextProps.__showMobileMap !== state.__showMobileMap) {
            nextState.shouldRenderMobileMap = nextState.__showMobileMap = nextProps.__showMobileMap;
        }

        /* eslint-enable no-multi-assign */

        nextState.shouldLockScrolling = SearchResultsWithMap.shouldLockScrolling(
            nextState.shouldRenderRefinePanel,
            nextState.shouldRenderMobileMap
        );

        return nextState;
    }

    mapRef = React.createRef()

    constructor(props) {
        super(props);

        this.defaultMapLocation = get(props, 'theme.search.config.defaultMapLatitude') ? {
            latitude: props.theme.search.config.defaultMapLatitude,
            longitude: props.theme.search.config.defaultMapLongitude,
        } : undefined;

        const currentBreakpoint = getWindowBreakpoint();

        this.lastSearchRequestId = '-1';
        this.state = {
            /** caching so it's ready for gDSFP */
            __showMobileMap: props.__showMobileMap,
            __showRefinePanel: props.__showRefinePanel,

            searchBarExpanded: false,
            shouldHandleMapMove: false,
            shouldLockScrolling: false,

            shouldRenderOverlay: false,
            shouldRenderSortingTakeover: false,
            shouldRenderSortingTakeoverToggle: this.props.shouldRenderFacetedSearchBar
                && currentBreakpoint < Breakpoints.small,
            showSortingActivatedDot: false,

            /**
             * this makes sure the map still renders in screenshot testing mode
             * and initially during SSR to keep the page from jumping
             */
            shouldRenderDesktopMap: !!global.theme,
            shouldRenderMobileMap: props.__showMobileMap,
            shouldRenderMobileMapToggle: props.__showMobileMap,
            shouldRenderRefinePanel: props.__showRefinePanel || (
                currentBreakpoint >= Breakpoints.medium
                && props.theme.search.config.openDesktopRefinePanelOnLoad
            ),
        };
    }

    componentDidMount() {
        this.handleResize();
        this.maybeLogSearchView();
        window.addEventListener('resize', this.handleResize, true);
    }

    shouldComponentUpdate(nextProps, nextState) {
        return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);
    }

    componentDidUpdate(prevProps) {
        const prevTitle = get(prevProps, 'seo.title');
        const title = get(this.props, 'seo.title');
        if (title && title !== prevTitle) {
            document.title = title;
        }

        this.maybeLogSearchView();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize, true);
    }

    render() {
        return (
            <div>
                <ShowFor
                    xsmall
                    xxsmall
                >
                    {this.maybeRenderSearchPanel()}
                    {this.maybeRenderSortingTakeover()}
                    {this.maybeRenderMobileMap()}
                </ShowFor>

                {this.state.shouldLockScrolling && <ScrollLock />}

                {this.renderSearchBar()}

                <PrimaryContent data-test="search-content">
                    <SearchOverlay
                        data-test="faceted-search-overlay"
                        hidden={!this.state.shouldRenderOverlay}
                    />
                    <SearchResultContainer>
                        <RelativeContainer>
                            {this.maybeRenderSearchResultArea()}
                        </RelativeContainer>
                    </SearchResultContainer>

                    {this.renderAside()}
                </PrimaryContent>

                <StyledPagination
                    data-test="search-pagination"
                    maxNumberOfNumberedControls={7}
                    page={this.props.page}
                    pageCount={this.props.pageCount}
                    updateParameters={this.props.updateParameters}
                />

                {this.maybeRenderSeoBlurb()}
                {this.maybeRenderDisclaimer()}
            </div>
        );
    }

    renderOverlay = toShow => {
        if (toShow && !this.state.shouldRenderOverlay) {
            this.setState({ shouldRenderOverlay: true });
        } else if (this.state.shouldRenderOverlay) {
            this.setState({ shouldRenderOverlay: false });
        }
    }

    renderSearchBar() {
        if (this.props.shouldRenderFacetedSearchBar) {
            return (
                <div>
                    <FacetedSearch
                        data-test="faceted-search-bar"
                        facets={this.props.facets}
                        theme={this.props.theme}
                        updateParameters={this.props.updateParameters}
                        updateRenderOverlay={this.renderOverlay}
                    />

                    <MatchAsideSizing>
                        <Toggles>
                            {this.maybeRenderSortingToggle()}
                            {this.maybeRenderMobileMapToggle()}
                        </Toggles>
                    </MatchAsideSizing>
                </div>
            );
        }

        return (
            <RefineSearchBar>
                <RefineSearchMessage>
                    <FormattedMessage
                        defaultMessage="Filter by:"
                        id="Components.RefineSearchBar.Message"
                    />
                </RefineSearchMessage>

                {this.props.specialties.length > 0 && (
                    <SpecialtiesDropdown
                        data-test="search-specialties-dropdown"
                        onChangeCallback={this.handleParameterChange}
                        specialties={this.props.specialties}
                        specialtyId={this.props.searchSpecialtyId}
                    />
                )}

                <VisitReasonsDropdown
                    acceptedProcedures={this.props.acceptedProcedures}
                    data-test="search-visit-reason-dropdown"
                    disableProcedureCache={this.props.disableProcedureCache}
                    onChangeCallback={this.handleParameterChange}
                    procedureId={this.props.searchProcedureId}
                    specialtyId={this.props.searchSpecialtyId}
                    visitReasons={this.props.visitReasons}
                />

                <MatchAsideSizing>
                    <Toggles>
                        {this.maybeRenderRefineButton()}
                        {this.maybeRenderRefineCloseButton()}
                        {this.maybeRenderMobileMapToggle()}
                    </Toggles>
                </MatchAsideSizing>
            </RefineSearchBar>
        );
    }

    renderSearchMapComponents() {
        return [
            this.maybeRenderSearchByMovingMapToggle(),
            <ThemedStyledSearchMap
                key="search-map"
                controlPosition={MapControlsPosition.TOP_RIGHT}
                data-test="search-map"
                defaultLocation={this.defaultMapLocation}
                entities={this.props.providers}
                innerRef={this.mapRef}
                onClick={this.props.trackMapClick}
                onMapDragEnd={this.handleMapMovement}
                onMapExplicitZoom={this.handleMapMovement}
                providerLoading={this.props.loading}
                searchAddress={this.props.searchAddress}
                searchByMovingMapEnabled={this.state.shouldHandleMapMove}
            />,
        ];
    }

    renderAside() {
        /**
         * due to the way google maps works, the map needs to be fully unrendered if the outer container
         * is being hidden, or we will end up in a broken state
         *
         * we experimented with using some imperative methods to try and reset the map, but this was the
         * only implementation that worked consistently
         */
        return (
            <Aside data-test="search-sidebar">
                <StickySectionContainer>
                    <Sticky>
                        {this.maybeRenderSearchPanel()}
                        {this.state.shouldRenderDesktopMap && this.renderSearchMapComponents()}
                    </Sticky>
                </StickySectionContainer>
            </Aside>
        );
    }

    maybeRenderSearchPanel() {
        if (this.state.shouldRenderRefinePanel) {
            return (
                <RefineSearch
                    onPanelClose={this.handleCloseRefinePanel}
                    totalCount={this.props.totalCount}
                    updateParameters={this.props.updateParameters}
                    visitReasons={this.props.visitReasons}
                />
            );
        }
    }

    maybeRenderMobileMap() {
        if (this.state.shouldRenderMobileMap) {
            return (
                <MobileSearchMapContainer>
                    {this.renderSearchMapComponents()}
                </MobileSearchMapContainer>
            );
        }
    }

    maybeRenderSeoBlurb() {
        const { seo, theme } = this.props;

        if (seo && seo.blurb && theme.components.SeoBlurb.enabled) {
            return (
                <SeoBlurbContainer
                    component={SEO.SeoBlurb}
                    data-test="search-seo-blurb"
                    description={seo.blurb.description}
                    header={seo.blurb.header}
                />
            );
        }
    }

    maybeLogSearchView() {
        const { searchRequestId, trackSearchRequestId } = this.props;
        if (searchRequestId && this.lastSearchRequestId !== searchRequestId) {
            this.lastSearchRequestId = searchRequestId;
            trackSearchRequestId(searchRequestId);
        }
    }

    maybeRenderSearchResultArea() {
        const {
            doctorLocationIds,
            error,
            insuranceCarrierImage,
            isNewPatient,
            loading,
            providers,
            providersMappedByNetworkType,
            referrerType,
            searchProcedureId,
            searchSpecialtyId,
            seo,
            shouldRenderFacetedSearchBar,
            shouldRenderNetworkType,
            shouldRenderReviewCount,
            showInsurancePicker,
            governmentInsuranceData,
            telehealthAds,
            updateParameters,
            widget,
        } = this.props;

        const showGovernmentInsuranceNotice = get(governmentInsuranceData, 'showGovernmentInsuranceWarning');

        if (this.state.shouldRenderMobileMap) {
            /**
             * while the mobile map is open, you can't actually see the results anyway
             */
            return null;
        } else if (loading) {
            return (<Spinner data-test="search-loader" />);
        } else if (showGovernmentInsuranceNotice) {
            const {
                header,
                links,
                message,
            } = governmentInsuranceData;
            return (
                <GovernmentInsuranceNotice
                    data-test="search-government-insurance-notice"
                    header={header}
                    links={links}
                    message={message}
                />
            );
        } else if (error || (!providers.length && !telehealthAds.length)) {
            return (<NoResults data-test="search-no-results" />);
        }

        return (
            <SearchResults
                data-test="search-results"
                doctorLocationIds={doctorLocationIds}
                facets={this.props.facets}
                hasSeo={!!seo}
                insuranceCarrierImage={insuranceCarrierImage}
                isNewPatient={isNewPatient}
                onResultMouseout={this.handleResultInteract}
                onResultMouseover={this.handleResultInteract}
                providers={providersMappedByNetworkType}
                referrerType={referrerType}
                searchProcedureId={searchProcedureId}
                searchSpecialtyId={searchSpecialtyId}
                shouldRenderFacetedSearchBar={shouldRenderFacetedSearchBar}
                shouldRenderNetworkType={shouldRenderNetworkType}
                shouldRenderReviewCount={shouldRenderReviewCount}
                showInsurancePicker={showInsurancePicker}
                telehealthAds={telehealthAds}
                updateParameters={updateParameters}
                widget={widget}
            />
        );
    }

    maybeRenderRefineButton() {
        if (this.props.shouldRenderRefineSearchButton) {
            return (
                <Toggle
                    data-active={this.state.shouldRenderRefinePanel}
                    data-test="search-refine-panel-button"
                    onClick={this.handleToggleRefinePanel}
                >
                    <Switchboard color="currentColor" />

                    {this.props.refinementCount === 0 ? [
                        <HideForInline
                            key="desktop"
                            xsmall
                            xxsmall
                        >
                            <FormattedMessage
                                defaultMessage="more filters"
                                id="Components.RefineSearch.Refine.Desktop"
                            />
                        </HideForInline>,

                        <ShowForInline
                            key="mobile"
                            xsmall
                            xxsmall
                        >
                            <FormattedMessage
                                defaultMessage="Filter"
                                id="Components.RefineSearch.Refine.Mobile"
                            />
                        </ShowForInline>,
                    ] : [
                        <HideForInline
                            key="desktop-multi"
                            xsmall
                            xxsmall
                        >
                            <FormattedMessage
                                defaultMessage="more filters ({count} applied)"
                                id="Components.RefineSearch.RefineGTZero.Desktop"
                                values={{ count: this.props.refinementCount }}
                            />
                        </HideForInline>,

                        <ShowForInline
                            key="mobile-multi"
                            xsmall
                            xxsmall
                        >
                            <FormattedMessage
                                defaultMessage="Filter ({count})"
                                id="Components.RefineSearch.RefineGTZero.Mobile"
                                values={{ count: this.props.refinementCount }}
                            />
                        </ShowForInline>,
                    ]}
                </Toggle>
            );
        }
    }

    maybeRenderRefineCloseButton() {
        if (this.state.shouldRenderRefinePanel) {
            return (
                <CloseRefineButton
                    data-test="search-refine-panel-close-button"
                    onClick={this.handleCloseRefinePanel}
                >
                    <FormattedMessage
                        defaultMessage="close"
                        id="Components.RefineSearch.Close"
                    />
                    <Arrow
                        color="currentColor"
                        direction="up"
                        height={8}
                    />
                </CloseRefineButton>
            );
        }
    }

    maybeRenderSortingToggle() {
        if (this.state.shouldRenderSortingTakeoverToggle) {
            return (
                <SortByToggle onClick={this.handleClickSortByToggleClicked}>
                    <FormattedMessage
                        defaultMessage={SORT_BY}
                        id="Components.Search.MobileSortByToggle"
                    />
                    {this.maybeRenderSortingEnabled()}
                </SortByToggle>
            );
        }
    }

    maybeRenderSortingEnabled() {
        if (this.state.showSortingActivatedDot) {
            return (
                <Dot
                    color={this.props.theme.header.textColor}
                />
            );
        }
    }

    handleRenderSortingEnabled = toShow => {
        if (toShow && !this.state.showSortingActivatedDot) {
            this.setState({ showSortingActivatedDot: true });
        } else if (this.state.showSortingActivatedDot) {
            this.setState({ showSortingActivatedDot: false });
        }
    }

    handleClickSortByToggleClicked = () => {
        this.props.trackSortingDropdownClick();
        this.setState({ shouldRenderSortingTakeover: true });
    }

    handleExitSortByTakeover = () => {
        this.setState({ shouldRenderSortingTakeover: false });
    }

    maybeRenderSortingTakeover() {
        if (this.state.shouldRenderSortingTakeover) {
            return (
                <SortingTakeover
                    changeSortingEnabled={this.handleRenderSortingEnabled}
                    exitSortByTakeover={this.handleExitSortByTakeover}
                    theme={this.props.theme}
                    updateParameters={this.props.updateParameters}
                />
            );
        }
    }

    maybeRenderMobileMapToggle() {
        const { viewPageMessage } = this.props;
        if (this.state.shouldRenderMobileMapToggle) {
            return (
                <Toggle
                    data-test="search-mobile-map-toggle"
                    onClick={this.handleToggleMobileMap}
                >
                    {this.state.shouldRenderMobileMap ? viewPageMessage
                        : (<FormattedMessage
                            defaultMessage={VIEW_MAP}
                            id="Components.Search.MobileMapToggle"
                        />)}
                </Toggle>
            );
        }
    }

    maybeRenderSearchByMovingMapToggle() {
        if (this.props.shouldRenderSearchByMovingMap) {
            return (
                <MapOverlay key="search-map-overlay">
                    <Checkbox
                        checked={this.state.shouldHandleMapMove}
                        data-test="search-by-moving-map-toggle"
                        onChecked={this.handleToggleMapMoveTracking}
                    >
                        search by moving map
                    </Checkbox>
                </MapOverlay>
            );
        }
    }

    maybeRenderDisclaimer() {
        if (this.props.theme.search.disclaimer) {
            return (
                <Layout.PaddedContainer>
                    <HorizontalRule />
                    <Markdown
                        data-test="search-disclaimer"
                        source={this.props.theme.search.disclaimer}
                    />
                </Layout.PaddedContainer>
            );
        }
    }

    handleResize = throttle(() => {
        const currentBreakpoint = getWindowBreakpoint();
        const nextState = { ...this.state };

        if (this.state.searchBarExpanded && currentBreakpoint >= Breakpoints.medium) {
            nextState.searchBarExpanded = false;
        }

        if (!this.state.shouldRenderDesktopMap && currentBreakpoint >= Breakpoints.small) {
            nextState.shouldRenderDesktopMap = true;
        } else if (this.state.shouldRenderDesktopMap && currentBreakpoint < Breakpoints.small) {
            nextState.shouldRenderDesktopMap = false;
        }

        if (this.state.shouldRenderMobileMap && currentBreakpoint >= Breakpoints.small) {
            nextState.shouldRenderMobileMap = false;
        }

        if (!this.state.shouldRenderMobileMapToggle && currentBreakpoint < Breakpoints.small) {
            nextState.shouldRenderMobileMapToggle = true;
        } else if (this.state.shouldRenderMobileMapToggle && currentBreakpoint >= Breakpoints.small) {
            nextState.shouldRenderMobileMapToggle = false;
        }

        const desiredState = SearchResultsWithMap.shouldLockScrolling(
            nextState.shouldRenderRefinePanel,
            nextState.shouldRenderMobileMap,
        );

        if (desiredState !== this.state.shouldLockScrolling) {
            nextState.shouldLockScrolling = desiredState;
        }

        this.setState(nextState);
    }, 500);

    handleResultInteract = event => {
        if (this.mapRef.current) {
            this.mapRef.current.triggerMarkerEvent(
                event.type, event.currentTarget.getAttribute('data-location-id')
            );
        }
    };

    handleParameterChange = value => this.props.updateParameters(value);

    handleToggleRefinePanel = () => {
        const shouldRenderRefinePanel = !this.state.shouldRenderRefinePanel;

        if (shouldRenderRefinePanel) {
            this.props.trackMoreFiltersClick();
        }

        this.setState(prevState => ({
            shouldLockScrolling: SearchResultsWithMap.shouldLockScrolling(
                shouldRenderRefinePanel,
                prevState.shouldRenderMobileMap,
            ),
            shouldRenderRefinePanel,
        }));
    }

    handleCloseRefinePanel = () => this.setState(prevState => ({
        shouldLockScrolling: SearchResultsWithMap.shouldLockScrolling(
            false,
            prevState.shouldRenderMobileMap,
        ),

        shouldRenderRefinePanel: false,
    }));

    handleToggleMobileMap = () => {
        const action = this.state.shouldRenderMobileMap ? this.props.viewPageMessage : VIEW_MAP;
        this.props.trackMobileMapToggleClick(action);
        this.setState(prevState => ({
            shouldLockScrolling: SearchResultsWithMap.shouldLockScrolling(
                undefined,
                !prevState.shouldRenderMobileMap,
            ),

            shouldRenderMobileMap: !prevState.shouldRenderMobileMap,
        }));
    };

    handleToggleMapMoveTracking = checked => this.setState({ shouldHandleMapMove: checked });

    handleMapMovement = latlng => {
        if (this.state.shouldHandleMapMove) {
            const mapBounds = latlng.toJSON();
            const center = latlng.getCenter().toJSON();

            this.props.updateParameters({
                geoShape: {
                    northLatitude: mapBounds.north,
                    southLatitude: mapBounds.south,
                    westLongitude: mapBounds.west,
                    eastLongitude: mapBounds.east,
                    centerLatitude: center.lat,
                    centerLongitude: center.lng,
                },
            });
        }
    };

    expandSearchBar = () => this.setState({ searchBarExpanded: true });
}

const HideForInline = HideFor.withComponent('span');
const ShowForInline = ShowFor.withComponent('span');

const Spinner = styled(UnstyledSpinner)`
    min-height: 300px;
    ${breakpoint('xsmall', { down: true })} {
        position: static;
    }
`;

const PrimaryContent = styled(StickyContainer)`
    display: flex;
    position: relative;
`;

const SearchOverlay = styled.div`
    background: white;
    bottom: 0;
    height: 100%;
    left: 0;
    opacity: 0.8;
    position: absolute;
    right: 0;
    top: 0;
    width: 100%;
    z-index: 100;

    ${breakpoint('xsmall', { down: true })} {
        display: none;
    }
`;

const SearchResultContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow: hidden;
`;

const StickySectionContainer = styled.div`
    position: relative;
`;

const sidebarSizingCSS = css`
    flex: 0 0;
    ${breakpoint.small} {
        flex-basis: ${ViewConstants.MAP_WIDTH_S};
    }
    ${breakpoint.large} {
        flex-basis: ${ViewConstants.MAP_WIDTH_L};
    }
    ${breakpoint.xlarge} {
        flex-basis: ${ViewConstants.MAP_WIDTH_XL};
    }
    @media screen and (min-width: 1980px) {
        flex-basis: ${ViewConstants.MAP_WIDTH_XXXL};
    }
`;

const Aside = styled.aside`
    display: none; // screenshot testing renders the aside always, so need to hide it still
    ${breakpoint.small} {
        border: 0;
        display: block;
        margin-right: 0;
        overflow: hidden;
    }
    ${sidebarSizingCSS}
`;

const FILTER_PADDING_RIGHT = 20;

const RefineSearchMessage = styled(Layout.PaddedContainer)`
    white-space: nowrap;
    flex-shrink: 0;
    padding-right: ${FILTER_PADDING_RIGHT}px;
    ${breakpoint('xsmall', { down: true })} {
        display: none;
    }
`;

export const VisitReasonsDropdown = styled(UnstyledVisitReasonsDropdown)`
    ${breakpoint('xsmall', { down: true })} {
        display: none !important;
    }
`;

export const SpecialtiesDropdown = styled(UnstyledSpecialtiesDropdown)`
    padding-right: ${FILTER_PADDING_RIGHT}px;
    ${breakpoint('xsmall', { down: true })} {
        display: none !important;
    }
`;

export const RefineSearchBar = styled.div`
    align-items: center;
    background: ${p => p.theme.search.header.filters.bgColor};
    color: ${p => p.theme.search.header.filters.textColor};
    display: flex;
    font-family: ${FontFamily.SharpSansBold};
    font-size: 15px;
    height: 0;
    letter-spacing: 0.16px;
    position: static;
    ${breakpoint.small} {
        height: 48px;
    }
`;

export const MatchAsideSizing = styled.div`
    ${sidebarSizingCSS}
    align-self: stretch;
    display: flex;
    margin-left: auto;
    background-color: ${p => p.shouldRenderFacetedSearchBar ? white : p.theme.search.header.filters.bgColor}
`;

const Toggles = styled(MobileHeaderToggles)`
    ${breakpoint.small} {
        border-left: 1px solid #FFF;
        display: flex;
        line-height: 1;
        max-width: ${ViewConstants.REFINE_MAX_WIDTH}px;
        padding: 0 30px;
        position: static;
        width: 100%;
    }
`;

const buttonCSS = css`
    background: none;
    border: 0;
    cursor: pointer;
    padding: 0;
    text-align: left;
    white-space: nowrap;
`;

const Toggle = styled.button`
    ${buttonCSS}
    padding: 0;
    text-align: left;
    white-space: nowrap;
    color: ${p => p.theme.header.textColor};
    font: 13px ${FontFamily.SharpSansSemibold};
    &:active {
        color: ${p => p.theme.search.header.filters.toggleColorActive};
    }
    &[data-active="true"] {
        color: ${p => p.theme.search.header.filters.toggleColorActive} !important;
    }
    > span {
        line-height: 1;
    }
    & + & {
        margin-left: 10px;
    }
    ${breakpoint.small} {
        color: ${p => p.theme.search.header.filters.textColor};
        font-family: ${FontFamily.SharpSansBold};
        > span {
            vertical-align: middle;
        }
    }
    ${breakpoint.medium} {
        flex-basis: auto;
    }
`;

const SortByToggle = styled.button`
    ${buttonCSS}
    color: ${p => p.theme.header.textColor};
    font: 13px ${FontFamily.SharpSansSemibold};
    margin-right: 15px;
    padding: 0;
    text-align: left;
    white-space: nowrap;
    &:active {
        color: ${p => p.theme.search.header.filters.toggleColorActive};
    }
    ${breakpoint.small} {
        display: none;
    }
`;

const Dot = styled(UnstyledDot)`
    height: 7px;
    margin-left: 3px;
    padding-bottom: 1px;
    white-space: nowrap;
    width: 9px;
    ${breakpoint.small} {
        display: none;
    }
`;

const CloseRefineButton = styled.button`
    ${buttonCSS}
    color: ${p => p.theme.search.header.filters.toggleColorActive};
    cursor: pointer;
    display: none;
    font: 13px ${FontFamily.SharpSansSemibold};
    margin-left: auto;
    ${breakpoint.small} {
        display: inline-block;
    }
`;

const Arrow = styled(UnstyledArrow)`
    margin-left: 9px;
`;

const Switchboard = styled(UnstyledSwitchboard)`
    display: none;
    font-size: 14px;
    margin: 0 12px 0 0;
    ${breakpoint.small} {
        display: inline-block;
        vertical-align: middle;
    }
`;

// safari doesn't accept the standard "transparent" keyword here
const TRANSPARENT = 'rgba(255, 255, 255, 0)';
const TRANSPARENT_WHITE = 'rgba(255, 255, 255, 0.8)';

const MapOverlay = styled.div`
    align-items: center;
    background: linear-gradient(to right, ${TRANSPARENT_WHITE} 0%, ${TRANSPARENT_WHITE} 50%, ${TRANSPARENT} 100%);
    display: flex;
    padding: 0 32px 0 22px;
    position: absolute;
    top: 0;
    left: 0;
    right: 48px;
    height: ${ViewConstants.HEADER_BAR_HEIGHT_XXSMALL}px;
    z-index: 1;
    ${breakpoint.medium} {
        height: ${ViewConstants.HEADER_BAR_HEIGHT_MEDIUM}px;
    }
`;

const Checkbox = styled(UnstyledCheckbox)`
    color: ${navy};
    font: 14px/1.5333 ${FontFamily.SharpSansSemibold};
`;

const StyledPagination = styled(SearchPagination)`
    flex-grow: 1;
    padding-top: 38px;
    padding-bottom: 44px;
`;

const SeoBlurbContainer = styled(Layout.PaddedContainer)`
    margin-bottom: 40px;
    ${breakpoint.small} {
        width: calc(100% - ${ViewConstants.MAP_WIDTH_S});
    }
    ${breakpoint.large} {
        width: calc(100% - ${ViewConstants.MAP_WIDTH_L});
    }
    ${breakpoint.xlarge} {
        width: calc(100% - ${ViewConstants.MAP_WIDTH_XL});
    }
    @media screen and (min-width: 1980px) {
        width: calc(100% - ${ViewConstants.MAP_WIDTH_XXXL});
    }
`;

const RelativeContainer = styled.div`
    flex-grow: 1;
    position: relative;
`;

const HorizontalRule = styled.hr`
    border-top-color: ${grey75};
    border-style: ridge;
    margin: 0;
`;

const Markdown = styled(UnstyledMarkdown)`
    color: ${navy};
    font: 14px/20px ${FontFamily.KievitSlabBook};
    padding: 31px 0 34px;
`;

const ThemedUnstyledSearchMap = withTheme(SearchMap);
const ThemedStyledSearchMap = styled(ThemedUnstyledSearchMap)`
    height: 100vh;
    position: relative;
`;

/**
 * this is unfortunately a complicated layout
 *
 * the `top` value is meant to reveal only the logo and toggle buttons in the header but occlude the
 * search bar, per the comps
 */
const MobileSearchMapContainer = styled.div`
    position: fixed;
    top: ${p => Layout.HEADER_Y_AXIS_PADDING + getLogoSizingAtBreakpoint(p.theme, 'xxsmall').height + ViewConstants.HEADER_SEARCH_BAR_TOP_MARGIN}px;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: ${ViewConstants.MOBILE_MAP_ZINDEX};
    ${breakpoint.xsmall} {
        ${p => {
            const h = getLogoSizingAtBreakpoint(p.theme, 'xsmall').height;
            return h ? css`top: ${Layout.HEADER_Y_AXIS_PADDING + h + ViewConstants.HEADER_SEARCH_BAR_TOP_MARGIN}px;` : '';
        }}
    }
`;
